// assistantFlyStart.js — FLY'S FIRST SCREEN, made from each workspace.
//
// Nika 2026-10-22: "give fly initial screen to start from based on each
// workspace". Before this an empty Fly canvas said one line — "Open an app,
// add a section, or ask for a page." — the same in every workspace. Now it
// opens the way keepz--fly does (the reference product, docs/FLY_MODE.md): a
// headline that names THIS workspace, then up to six tiles, each a page one
// tap away (components/AssistantFlyView.js AssistantFlyStart).
//
// THE TILES come from the workspace's own data — nothing here names a tenant
// and nothing is written per workspace by hand:
//
//   FIGURE tiles  what needs the person, live: the home's signals
//                 (HOME_SIGNALS_CONTRACT v1, functions/homeSignals.js — the
//                 installed apps' own `homeSignals` providers and the built-in
//                 tasks / calendar / mail ones; counts only). The signals of
//                 one app and one KIND make ONE tile: the first one's count
//                 and words, "+n more", the app's name, and the kind's ask
//                 ("CRM: what is overdue?"). The page is asked with every
//                 signal of the group as its facts.
//   ASK tiles     this workspace's starters (functions/assistantStarters.js
//                 workspaceStarters): the generated set written for THIS
//                 workspace in its language, else the built set — the
//                 installed apps' own manifest starters first, one ask per
//                 app it shows, the generic staple last.
//
// Figures first (at most three), the asks after them; the first tile is the
// large one. As many as fill whole rows on the dashboard's columns
// (assistantFlyStartTracks): six on three tracks and on a phone, 2 × tracks − 3
// from four tracks up (the large tile's two rows beside it). Until the first
// signals pass has answered (≤ 800 ms, homeSignalsTick's budget) the slots
// hold skeletons, so a figure landing late never reorders tiles a person is
// aiming at (Rule 66). A tile asks the way a page's next-ask chip does
// (assistantFlyAsk); a starter that carries a `localAction` runs it, as it
// does in the chat.
//
// THE TOUR (Nika 2026-10-28: "take me to symbols tour … also add it in
// recommended options"): where the shell runs the quick tour
// (assistantFlyTourOffered), the LAST tile is "Take the tour" — its press
// runs aiStartTour (the home, then the stops), never a page ask. It takes
// the last slot, so the asks keep every slot before it.
//
// THE SUMMARY FIRST (Nika 2026-10-22: "fly should first show current summary
// when its empty on each workspace according to their data"): above the tiles
// the screen says where this workspace stands — the home's greeting
// (assistantFlyStartGreeting) and the dashboard's own summary (components/
// HomeTopSummary.js HomeSummaryBrief over functions/homeSummaryCompose.js: the
// same signals, its AI sentences or its template, the same Updated and
// Refresh), or one calm line when nothing needs the person
// (assistantFlyStartCalm).
//
// FRANK: every export is self-contained — no import, no module constant, no
// bare sibling call (`this.call`). `this` is the calling element.

// The canvas an element sits in is the HOME's (pages/main.js mounts it in the
// grid area), not the page layer's over another route (components/
// AssistantMainLayer.js, `mainHost: 'over'`). Read from the tree, once — the
// host of a mounted view never changes.
export const assistantFlyOnHome = function assistantFlyOnHome(el) {
  let node = el || this
  for (let i = 0; node && i < 16; i++) {
    if (node.key === 'AssistantFlyView') return node.mainHost !== 'over'
    node = node.parent
  }
  return false
}

// Does this shell run the quick tour? The assistant's entry (aiStartTour) and
// the tour itself (onboardingTourStart) are registered — the same test
// assistantRecords.js assistantClientCapabilities declares `tour` by. The
// start screen's tour tile and a page's tour follow-up (functions/
// assistantFlyFollow.js) show only then.
export const assistantFlyTourOffered = function assistantFlyTourOffered() {
  const fns = (this && this.context && this.context.functions) || {}
  return typeof fns.aiStartTour === 'function' && typeof fns.onboardingTourStart === 'function'
}

// The canvas shows and its session holds no page: the start screen's gate.
export const assistantFlyStartShown = function assistantFlyStartShown(root) {
  const r = root && typeof root === 'object' ? root : {}
  const shown = !!this.call('assistantFlyShown', r)
  const page = this.call('assistantFlyPage', r)
  return shown && !page
}

// The tiles (or their skeletons). Every row carries EVERY key the tile
// reads: a childrenAs-'state' child falls back to ROOT state for a key it
// lacks.
export const assistantFlyStartTiles = function assistantFlyStartTiles(root, tracks) {
  const r = root && typeof root === 'object' ? root : {}
  // Whole rows on the tracks the screen has: six on three (and on a phone),
  // the large tile's two rows filled beside it from four tracks up.
  const n = Math.min(6, Math.max(3, Math.floor(Number(tracks)) || 3))
  const MAX = n >= 4 ? 2 * n - 3 : 6
  const MAX_FIGURES = 3
  // A kind's ask — every key written out (the i18n gate reads literals);
  // 'new' is the built set's own "{app}: what is new?".
  const ASK = {
    overdue: 'workspace.flyStart.ask.overdue',
    dueToday: 'workspace.flyStart.ask.dueToday',
    upcoming: 'workspace.flyStart.ask.upcoming',
    waiting: 'workspace.flyStart.ask.waiting',
    new: 'workspace.aiStarter.app.whatsNew',
    risk: 'workspace.flyStart.ask.risk'
  }
  const ws = r.workspace
  const workspaceId = (ws && String(ws.id || ws._id || '')) || ''
  const st = r._homeSignals
  if (!workspaceId) return []
  const word = (key) => {
    const v = this.call('polyglot', key)
    return v == null ? '' : String(v)
  }
  const blank = {
    key: '',
    kind: 'ask',
    hero: false,
    text: '',
    prompt: '',
    count: '',
    words: '',
    severity: '',
    signal: '',
    app: '',
    icon: 'magicstar',
    more: '',
    localAction: '',
    form: '',
    // the app an ask is pinned to (assistantStarters.js generatedStarterRows):
    // its route, its name, a built app's project id — assistantFlyStartAsk
    route: '',
    appName: '',
    appId: '',
    span: 1,
    tall: false
  }
  // Keys are element keys: letters and digits only, never an HTML tag name.
  const safe = (v) => String(v == null ? '' : v).replace(/[^A-Za-z0-9]+/g, '')
  // WHOLE ROWS, NEVER A HOLE: fewer tiles than the rows hold (the dashboard's
  // six tracks, eight tiles) left an empty slot at the end. The large tile is
  // two rows tall only when the others fill its first row beside it, and the
  // LAST tile spans the cells its row has left (`span`); a lone large tile
  // takes the whole row. A phone's two tracks are the tile grid's own rule.
  const shape = (list) => {
    const c = list.length
    const rest = c - 1
    const tall = rest > n - 2
    const cells = (tall ? 4 : 2) + rest
    const rows = Math.max(tall ? 2 : 1, Math.ceil(cells / n))
    const hole = rows * n - cells
    return list.map((t, i) => ({
      ...t,
      hero: i === 0,
      tall: i === 0 && tall,
      span: i === 0 ? (c === 1 ? n : 2) : i === c - 1 && hole > 0 && hole < n ? 1 + hole : 1
    }))
  }
  const pending = !st || typeof st !== 'object' || String(st.ws) !== workspaceId || !st.windowAt
  if (pending) {
    return shape(
      Array.from({ length: MAX }, (_, i) => ({
        ...blank,
        key: 'flyStartSkeleton' + i,
        kind: 'skeleton'
      }))
    )
  }

  // The app a signal belongs to, by its list's route: the launcher's own
  // tiles (assistantStarters.js visibleStarterTiles — the flag / access gate
  // already applied). Its name, or the workspace's when no tile matches.
  const tiles = this.call('visibleStarterTiles', r) || []
  const scope = String(this.call('assistantScopeName', r) || '')
  const appOf = (sig) => {
    const href = typeof sig.href === 'string' ? sig.href : ''
    const seg = href.split(/[?#]/)[0].split('/').filter(Boolean)[0] || ''
    const route = seg ? '/' + seg : ''
    const tile = tiles.find((t) => t && t.route === route) || null
    // An installed app's tile (it carries `appKey`) by its App Store name,
    // through installedAppName (Nika 2026-10-23: "app names = what a person
    // sees in the workspace App Store") — "Insights: what is overdue?", never
    // the manifest's "Analytics". A house tile's key and a customer's own
    // words read through polyglot, as before.
    const named =
      tile && tile.appKey
        ? String(this.call('installedAppName', { appKey: tile.appKey, title: tile.title }) || '')
        : ''
    return {
      name: tile ? named || word(tile.title) || String(tile.title || '') : '',
      icon: tile && tile.icon ? tile.icon : 'magicstar'
    }
  }

  const out = []
  const seenAsk = new Set()
  // FIGURES — one tile per app × kind, in the signals' own order (severity,
  // then count, then the App strip's order).
  const view = this.call('homeSignalsView', r) || {}
  const list = Array.isArray(view.list) ? view.list : []
  const groups = []
  for (const sig of list) {
    if (!sig || !ASK[sig.kind]) continue
    const id = sig.app + '|' + sig.kind
    const at = groups.find((g) => g.id === id)
    if (at) at.signals.push(sig)
    else groups.push({ id, signals: [sig] })
  }
  for (const g of groups.slice(0, MAX_FIGURES)) {
    const top = g.signals[0]
    const app = appOf(top)
    // The ask names the app, else the workspace; with neither it would read
    // ": what is overdue?" — that figure waits for a name instead.
    const named = app.name || scope
    if (!named) continue
    const text = word(ASK[top.kind]).replace('{app}', named)
    const facts = g.signals.map((x) => String(this.call('homeSignalsText', x) || '')).join(' · ')
    const more =
      g.signals.length > 1
        ? word('workspace.flyStart.more').replace('{n}', String(g.signals.length - 1))
        : ''
    seenAsk.add(text.trim().toLowerCase())
    out.push({
      ...blank,
      key: 'flyStartSignal' + safe(g.id),
      kind: 'figure',
      text,
      prompt: facts ? text + ' (' + facts + ')' : text,
      count: String(top.count),
      words: String(this.call('homeSignalsText', top, { bare: true }) || ''),
      severity: top.severity || 'info',
      signal: top.id || '',
      app: app.name,
      icon: this.call('homeSignalIcon', top) || 'magicstar',
      more
    })
  }

  // ASKS — this workspace's starters, the ones a figure already asks left out
  // (the last slot kept for the tour where the shell runs it).
  const tour = !!this.call('assistantFlyTourOffered')
  const room = tour ? MAX - 1 : MAX
  const rows = this.call('workspaceStarters', r) || []
  for (const row of rows) {
    if (out.length >= room) break
    const text = String((row && row.text) || '').trim()
    if (!text || seenAsk.has(text.toLowerCase())) continue
    seenAsk.add(text.toLowerCase())
    out.push({
      ...blank,
      key: 'flyStartAsk' + safe(row.key || text),
      kind: 'ask',
      text,
      prompt: String(row.prompt || text),
      app: String(row.caption || ''),
      icon: row.icon || 'magicstar',
      localAction: typeof row.localAction === 'string' ? row.localAction : '',
      // a starter that opens an app's own form (assistantStarterForm)
      form: typeof row.form === 'string' ? row.form : '',
      route: typeof row.route === 'string' ? row.route : '',
      appName: typeof row.appName === 'string' ? row.appName : '',
      appId: typeof row.appId === 'string' ? row.appId : ''
    })
  }
  // THE TOUR — the onboarding's own words: "Take the tour", "Tour" beside its
  // glyph, the one-minute hint on hover; its press runs aiStartTour.
  if (tour) {
    out.push({
      ...blank,
      key: 'flyStartTour',
      kind: 'ask',
      text: word('workspace.onboarding.replayTour'),
      prompt: word('workspace.onboarding.wayTourHint'),
      app: word('workspace.onboarding.tour.endEyebrow'),
      icon: 'playOval',
      localAction: 'aiStartTour'
    })
  }
  return shape(out.slice(0, MAX))
}

// A tile's tap: a starter's local action, else a page of the session — the
// next-ask chip's own path (assistantFlyAsk → assistantFlyGenerate). A tile
// pinned to a BUILT app (/x/<key>, its project id) asks about THAT app: its
// scope rides with the ask (the composer's own shape, functions/aiScope.js
// aiScopePayload), so the page reads the app's own collections — never a
// preset app's (dev walks round 5, 2026-10-29: tomas's "Check dock
// scheduling board for today" read the Scheduling app).
export const assistantFlyStartAsk = function assistantFlyStartAsk(s) {
  const tile = s && typeof s.parse === 'function' ? s.parse() : s
  if (!tile || typeof tile !== 'object' || tile.kind === 'skeleton') return null
  const fns = (this.context && this.context.functions) || {}
  if (tile.localAction && typeof fns[tile.localAction] === 'function') {
    // the tile rides along (assistantStarterForm reads its `form`)
    return this.call(tile.localAction, tile)
  }
  const prompt = String(tile.prompt || tile.text || '').trim()
  if (!prompt) return null
  const route = typeof tile.route === 'string' ? tile.route : ''
  const id = typeof tile.appId === 'string' ? tile.appId : ''
  const name = typeof tile.appName === 'string' ? tile.appName.trim() : ''
  if (!id || !name || !/^\/x\/[^/?#]+/.test(route)) return this.call('assistantFlyAsk', prompt)
  return this.call('assistantFlyAsk', {
    prompt,
    aiScope: { kind: 'app', label: name, route, app: { id, name, route } }
  })
}

// THE RECENT PAGES the start screen offers (Nika 2026-10-23: "make fly starter
// screen more advanced"): the pages a fresh Fly put away (assistantFlyNew —
// the logo, the home icon, New), newest first; each is its ask (a press asks
// it again: a new page on live data, assistantFlyStartAsk) and when it was
// made. This session's only — another workspace's never shows.
export const assistantFlyStartRecent = function assistantFlyStartRecent(root) {
  const fly = this.call('assistantFlySession', root)
  const list = fly && Array.isArray(fly.recent) ? fly.recent : []
  return list
    .filter((p) => p && typeof p.prompt === 'string' && p.prompt.trim())
    .slice(0, 6)
    .map((p, i) => ({
      key: 'recent-' + i,
      kind: 'ask',
      prompt: p.prompt,
      title: String(p.title || p.prompt),
      at: Number(p.at) || 0
    }))
}

// NEXT UP (Nika 2026-10-23: "expand fly empty screen functionality"): what
// is next for the person — the dashboard's Next up, the same merged list
// (functions/homeNextUp.js homeNextUpItems): their calendar events (by
// invitation or as organiser, under way first) AND the installed apps' own
// upcoming items (`homeUpcoming` providers — the CRM's viewings, …; the
// part's pass, `upcoming`, assistantFlyStartNextCollect), soonest first — at
// most `limit` (3). One row each: when ("in 12 min", "now", the weekday), the
// title, its times (and an app item's place), and its ONE action — an event's
// Join when it has a call, else Open (its sheet); an app item's Open (its
// page). A row carries scalars only (a row's object would be state): the
// press finds its event again, or takes the item's route
// (assistantFlyStartEventOpen). Read keys: the calendar window, the person,
// the pass (reactive callers).
export const assistantFlyStartNext = function assistantFlyStartNext(root, limit, upcoming) {
  const r = root && typeof root === 'object' ? root : {}
  const fns = (this && this.context && this.context.functions) || {}
  if (typeof fns.homeNextUpItems !== 'function') return []
  const want = Math.floor(Number(limit))
  const max = want > 0 ? Math.min(want, 6) : 3
  const now = Date.now()
  const pass = upcoming && typeof upcoming === 'object' ? upcoming : null
  const items = this.call('homeNextUpItems', r, now, pass) || []
  return items.slice(0, max).map((x, i) => {
    const event = x.event || null
    const hero = this.call('homeNextUpHero', x, now) || {}
    const action = this.call('homeNextUpAction', x) || {}
    const time = String(this.call('homeNextUpTime', x) || '')
    const place = !event && typeof x.place === 'string' ? x.place.trim() : ''
    return {
      key: 'next-' + String(x.key || i).replace(/[^A-Za-z0-9_-]/g, '_'),
      // every key a value (a row's empty key would read the root's)
      eventId: event ? String(event.id || '') : '',
      href: !event && typeof x.href === 'string' ? x.href : '',
      app: !event && typeof x.app === 'string' ? x.app : '',
      title: String(x.title || ''),
      when: [hero.before, hero.value, hero.after].filter(Boolean).join(' '),
      time: place ? (time ? time + ' · ' + place : place) : time,
      label: String(action.label || 'common.action.open'),
      call: String(action.call || (event ? 'openEventDetailModal' : 'homeNextUpOpen'))
    }
  })
}

// The apps' half of Next up: the providers' pass on the part `el` (its own
// state `upcoming`, homeNextUpCollect — a minute's TTL per workspace and
// provider set), asked only while the start screen SHOWS (it stays mounted,
// hidden, on every home) — on the shell's 30 s tick, when it shows, and when
// the installed apps land.
export const assistantFlyStartNextCollect = function assistantFlyStartNextCollect(el, s) {
  const host = el || this
  const root = (s && s.root) || (host && host.state && host.state.root)
  if (!host || typeof host.call !== 'function' || !root) return false
  if (!host.call('assistantFlyStartShown', root)) return false
  const fns = (host.context && host.context.functions) || {}
  if (typeof fns.homeNextUpCollect !== 'function') return false
  host.call('homeNextUpCollect')
  return true
}

// A Next up row, pressed: its action — an event's Join or Open on the event,
// found again in the calendar window (one that left it does nothing); an app
// item's Open on its own page (homeNextUpOpen → the shell's one opener).
export const assistantFlyStartEventOpen = function assistantFlyStartEventOpen(s) {
  const row = s && typeof s.parse === 'function' ? s.parse() : s
  const root = this.state && this.state.root
  if (!row || !row.call || !root) return false
  if (row.eventId) {
    const list = Array.isArray(root.intranetEvents) ? root.intranetEvents : []
    const event = list.find((e) => e && String(e.id) === String(row.eventId))
    if (!event) return false
    this.call(row.call, event)
    return true
  }
  if (row.href) {
    this.call('homeNextUpOpen', { href: row.href, app: row.app || '', title: row.title || '' })
    return true
  }
  return false
}

// The tile's accessible name: the severity in words (the dot is only a
// shape), the figure, the app, what it asks. Read off the tile's own fields,
// never a `.parse()` copy: a parse PEEKS (smbls signal store), so the tile's
// `ariaLabel` — this function's reactive caller — subscribed to nothing and
// kept its first words while the tile re-worded in place (dev 2026-10-25, a
// Georgian session: "Terminal: what is new?" on "ტერმინალი: რა სიახლეა?";
// __tests__/flyStartTileNameFollows.test.js). Every row carries every key
// (assistantFlyStartTiles), so no read falls back to the root.
export const assistantFlyStartLabel = function assistantFlyStartLabel(s) {
  if (!s || typeof s !== 'object' || s.kind === 'skeleton') return ''
  const SEVERITY = {
    urgent: 'workspace.home.signals.urgent',
    attention: 'workspace.home.signals.attention'
  }
  const parts = []
  if (s.kind === 'figure') {
    if (SEVERITY[s.severity]) parts.push(String(this.call('polyglot', SEVERITY[s.severity])))
    if (s.count) parts.push(s.count + ' ' + (s.words || ''))
  }
  if (s.app) parts.push(s.app)
  if (s.text) parts.push(s.text)
  return parts.join(' — ')
}

// The tiles' tracks: the dashboard's columns (the view's grid, functions/
// assistantFly.js assistantFlyMeasure), three to six — a phone's two are the
// tile grid's own media rule. `s` is the view's state (the screen inherits it).
export const assistantFlyStartTracks = function assistantFlyStartTracks(s) {
  const cols = Math.floor(Number(s && s.cols)) || 0
  return Math.min(6, Math.max(3, cols || 3))
}

// THE GREETING over the summary: the home's own words — the hour law and the
// first name of pages/main.js (`_greetLeadKey`, `_shellFirstName`; the two stay
// ONE law: __tests__/assistantFlyStart.test.js runs both over every hour). The
// lead-in carries its own separator when a name follows; without a name the
// greeting is the lead-in alone. → { lead, name }.
export const assistantFlyStartGreeting = function assistantFlyStartGreeting(s, now) {
  const at = new Date(Number(now) || Date.now())
  const hour = at.getHours()
  let person = null
  try {
    person = this.call('findMe', s)
  } catch (_) {
    person = null
  }
  const name =
    String((person && person.name) || '')
      .trim()
      .split(/\s+/)[0] || ''
  const has = !!name
  // Literal keys only (docs/I18N-CONVENTION.md: a key is never built).
  const key =
    hour >= 5 && hour < 12
      ? has
        ? 'workspace.home.greet.morning'
        : 'workspace.home.greet.morningNoName'
      : hour >= 12 && hour < 18
        ? has
          ? 'workspace.home.greet.afternoon'
          : 'workspace.home.greet.afternoonNoName'
        : hour >= 18 && hour < 22
          ? has
            ? 'workspace.home.greet.evening'
            : 'workspace.home.greet.eveningNoName'
          : has
            ? 'workspace.home.greet.night'
            : 'workspace.home.greet.nightNoName'
  const lead = this.call('polyglot', key)
  return { lead: lead == null ? '' : String(lead), name }
}

// The summary's calm line, when it has no sentence to say: every installed
// app answered and none needs the person ("Nothing needs you right now"), or
// an installed app will not come ("Some apps couldn't load" — never "nothing"
// then). '' while the summary speaks or waits (functions/homeSummaryCompose.js).
export const assistantFlyStartCalm = function assistantFlyStartCalm(root) {
  const status = (this.call('homeSummaryCompose', root) || {}).status
  const KEYS = {
    nothing: 'workspace.home.summary.none',
    unavailable: 'workspace.activation.partialTitle'
  }
  if (!Object.prototype.hasOwnProperty.call(KEYS, status)) return ''
  const words = this.call('polyglot', KEYS[status])
  return words == null ? '' : String(words)
}

// What the start screen needs, asked while it shows: a signals pass (fresh
// answers are not asked again — on the home the greeting's clock already
// keeps them; over another route nothing else would) and this workspace's
// generated starters (cached a week per person; the first open of the chat
// drawer asks the same). Both are idempotent and never block the screen.
export const assistantFlyStartPrime = function assistantFlyStartPrime(root) {
  const r = root || (this.state && this.state.root)
  if (!r || typeof r !== 'object') return false
  // Only for a start screen on screen: it stays mounted, hidden, on every
  // home, and a hidden one must never spend a model call.
  if (!this.call('assistantFlyStartShown', r)) return false
  const fns = (this.context && this.context.functions) || {}
  if (typeof fns.homeSignalsTick === 'function') {
    Promise.resolve()
      .then(() => this.call('homeSignalsTick', r))
      .catch((err) => console.warn('[fly] start signals pass failed:', err && err.message))
  }
  if (typeof fns.ensureWorkspaceStarters === 'function') {
    Promise.resolve()
      .then(() => this.call('ensureWorkspaceStarters', r, { surface: 'fly' }))
      .catch((err) => console.warn('[fly] start starters failed:', err && err.message))
  }
  return true
}
